import { useMemo } from "react";
import { useGetAvodDataQuery } from "../../store/api/analyticsApi";
import { extractData } from "../../utils/analytics/extractData";
import { groupData } from "../../utils/analytics/groupData";
import { processDateRangeData } from "../../utils/analytics/processDateRangeData";

const EMPTY_CHART = {
  xAxis: [],
  yAxis: [],
  percentageChange: 0,
  netValue: 0,
  formattedPercentageChange: "0%",
};

export const useAvodData = (dateRange, defaultChannel, isContentPartner = false, enabled = true) => {
  const params = useMemo(() => {
    if (!defaultChannel || !dateRange?.[0]) return null;
    return extractData(dateRange, defaultChannel);
  }, [dateRange, defaultChannel]);

  const skip = !params || !enabled;

  const { data: avodRawData, isFetching } = useGetAvodDataQuery(
    params
      ? {
          pubId: params.pub_id,
          channelId: params.channel_id,
          startDate: params.prev_start_date,
          endDate: params.end_date,
        }
      : {},
    { skip }
  );

  const parseTopLevelMetric = (value) => {
    if (typeof value === "string") {
      return parseInt(value.replace(/,/g, ""), 10) || 0;
    }
    return Number(value) || 0;
  };

  const totalRequests = useMemo(() => parseTopLevelMetric(avodRawData?.requests), [avodRawData]);
  const totalImpressions = useMemo(() => parseTopLevelMetric(avodRawData?.impressions), [avodRawData]);

  const createChart = (currentItems, previousItems, extractFn, isAverage = false) => {
    const currentSum = currentItems.reduce((sum, item) => sum + (extractFn(item) || 0), 0);
    const previousSum = previousItems.reduce((sum, item) => sum + (extractFn(item) || 0), 0);
    const currentValue = isAverage && currentItems.length ? currentSum / currentItems.length : currentSum;
    const previousValue = isAverage && previousItems.length ? previousSum / previousItems.length : previousSum;
    const percentageChange = previousValue === 0 ? (currentValue > 0 ? 100 : 0) : ((currentValue - previousValue) / previousValue) * 100;

    return {
      xAxis: currentItems.map((item) => item.request_timestamp),
      yAxis: currentItems.map(extractFn),
      netValue: Number.isInteger(currentValue) ? currentValue : currentValue.toFixed(2),
      percentageChange,
      formattedPercentageChange:
        percentageChange > 0
          ? `+${percentageChange.toFixed(1).replace(/\.0$/, "")}%`
          : `${percentageChange.toFixed(1).replace(/\.0$/, "")}%`,
    };
  };

  const contentPartnerCharts = useMemo(() => {
    if (!isContentPartner || !avodRawData?.data || !params) {
      return {
        avodRevenue: EMPTY_CHART,
        avodCPM: EMPTY_CHART,
        avodFillRate: EMPTY_CHART,
        avodRequests: EMPTY_CHART,
        avodImpressions: EMPTY_CHART,
      };
    }

    const aggregated = avodRawData.data.reduce((acc, item) => {
      const date = item.request_timestamp;
      if (!acc[date]) {
        acc[date] = {
          request_timestamp: date,
          totalRequest: 0,
          totalImpression: 0,
          revenue: 0,
          cpm: 0,
          fillrate: 0,
          count: 0,
        };
      }

      acc[date].totalRequest += item.totalRequest || 0;
      acc[date].totalImpression += item.totalImpression || 0;
      acc[date].revenue += item.revenue || 0;
      acc[date].cpm += item.cpm || 0;
      acc[date].fillrate += item.fillrate || 0;
      acc[date].count += 1;
      return acc;
    }, {});

    const aggregatedData = Object.values(aggregated).map((item) => ({
      ...item,
      cpm: item.count > 0 ? item.cpm / item.count : 0,
      fillrate: item.count > 0 ? item.fillrate / item.count : 0,
    }));

    const currentData = aggregatedData.filter(
      (item) => item.request_timestamp >= params.start_date && item.request_timestamp <= params.end_date
    );
    const previousData = aggregatedData.filter(
      (item) => item.request_timestamp >= params.prev_start_date && item.request_timestamp <= params.prev_end_date
    );

    return {
      avodRevenue: groupData(createChart(currentData, previousData, (entry) => entry.revenue || 0), dateRange),
      avodCPM: groupData(createChart(currentData, previousData, (entry) => entry.cpm || 0, true), dateRange),
      avodFillRate: groupData(createChart(currentData, previousData, (entry) => entry.fillrate || 0, true), dateRange),
      avodRequests: groupData(createChart(currentData, previousData, (entry) => entry.totalRequest || 0), dateRange),
      avodImpressions: groupData(createChart(currentData, previousData, (entry) => entry.totalImpression || 0), dateRange),
    };
  }, [isContentPartner, avodRawData, params, dateRange]);

  const standardCharts = useMemo(() => {
    if (isContentPartner || !avodRawData || !params) {
      return {
        avodRevenue: EMPTY_CHART,
        avodCPM: EMPTY_CHART,
        avodFillRate: EMPTY_CHART,
        avodRequests: EMPTY_CHART,
        avodImpressions: EMPTY_CHART,
      };
    }

    return {
      avodRevenue: groupData(
        processDateRangeData(avodRawData, { responsePath: "data", xAxisKey: "request_timestamp", extractValue: (entry) => parseFloat(entry.revenue) }, params),
        dateRange
      ),
      avodCPM: groupData(
        processDateRangeData(avodRawData, { responsePath: "data", xAxisKey: "request_timestamp", extractValue: (entry) => parseFloat(entry.cpm), isAverage: true }, params),
        dateRange
      ),
      avodFillRate: groupData(
        processDateRangeData(avodRawData, { responsePath: "data", xAxisKey: "request_timestamp", extractValue: (entry) => parseFloat(entry.fillrate), isAverage: true }, params),
        dateRange
      ),
      avodRequests: groupData(
        processDateRangeData(avodRawData, { responsePath: "data", xAxisKey: "request_timestamp", extractValue: (entry) => parseFloat(entry.totalRequest) }, params),
        dateRange
      ),
      avodImpressions: groupData(
        processDateRangeData(avodRawData, { responsePath: "data", xAxisKey: "request_timestamp", extractValue: (entry) => parseFloat(entry.totalImpression) }, params),
        dateRange
      ),
    };
  }, [isContentPartner, avodRawData, params, dateRange]);

  const charts = isContentPartner ? contentPartnerCharts : standardCharts;

  return {
    loading: enabled ? isFetching : false,
    avodRevenue: charts.avodRevenue,
    avodFillRate: charts.avodFillRate,
    avodCPM: charts.avodCPM,
    avodRequests: charts.avodRequests,
    avodImpressions: charts.avodImpressions,
    totalRequests,
    totalImpressions,
  };
};

export default useAvodData;
